import { useEffect, useState } from 'react';
import { ConfigProvider, Menu } from 'antd';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import style from '../../styles/sidebar.module.scss';
import headerStyle from '../../styles/header.module.scss';
import { useAuth } from '../../context/useAuth';
import { findActiveMenu, tabUrl } from '../../router/menu';
import { usePermissions } from '../../context/PermissionsContext';

const COLLAPSED_KEY = 'sidebarCollapsed';

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
};

// Admin main menu: one submenu per group, one item per page (tab) of the group.
const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  // Only the tabs the user's permissions allow; empty groups are already dropped.
  const { visibleMenuGroups: menuGroups } = usePermissions();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const active = findActiveMenu(location.pathname, location.search);
  const activeGroupKey = active?.group.key;
  const [openKeys, setOpenKeys] = useState(
    activeGroupKey ? [activeGroupKey] : []
  );

  // Close the profile actions after navigating (e.g. to /profile).
  useEffect(() => {
    setIsProfileOpen(false);
  }, [location.pathname]);

  // Open the current page's group when navigating into it (e.g. from a link on a page).
  useEffect(() => {
    if (activeGroupKey) setOpenKeys([activeGroupKey]);
  }, [activeGroupKey]);

  // Only one group open at a time: keep just the one that was opened last.
  const handleOpenChange = (keys) => {
    const opened = keys.find((key) => !openKeys.includes(key));
    setOpenKeys(opened ? [opened] : keys);
  };

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem(COLLAPSED_KEY, String(next));
    } catch {
      // Storage unavailable - the choice just isn't remembered.
    }
  };

  const itemKey = (group, tab) => `${group.key}/${tab.key}`;

  const handleClick = ({ key }) => {
    for (const group of menuGroups) {
      const tab = group.tabs.find((item) => itemKey(group, item) === key);
      if (tab) {
        navigate(tabUrl(tab));
        return;
      }
    }
  };

  return (
    <aside
      className={`${style.sidebar} ${collapsed ? `${style.sidebar__collapsed} sidebar--collapsed` : ''}`}
    >
      <div className={style.sidebar__top}>
        <img
          className={style.sidebar__logo}
          src="/logo.svg"
          alt="logo"
          height={42}
          width={42}
        />
        <button
          type="button"
          className={style.sidebar__toggle}
          onClick={toggleCollapsed}
          aria-label={collapsed ? 'Proširi meni' : 'Skupi meni'}
        >
          {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        </button>
      </div>
      <ConfigProvider
        theme={{
          token: { colorPrimary: '#005844' },
          components: {
            Menu: {
              itemSelectedBg: 'rgba(0, 88, 68, 0.08)',
              itemBg: 'transparent',
              // Fits the collapsed sidebar (80px minus its 8px side padding), so the
              // icons sit centred instead of overflowing.
              collapsedWidth: 64,
            },
          },
        }}
      >
        <Menu
          mode="inline"
          inlineCollapsed={collapsed}
          selectedKeys={active ? [itemKey(active.group, active.tab)] : []}
          // antd manages open submenus itself while collapsed (as popups).
          {...(collapsed ? {} : { openKeys, onOpenChange: handleOpenChange })}
          onClick={handleClick}
          items={menuGroups.map((group) => ({
            key: group.key,
            label: group.label,
            icon: group.icon,
            children: group.tabs.map((tab) => ({
              key: itemKey(group, tab),
              label: tab.label,
            })),
          }))}
          className={style.sidebar__menu}
        />
      </ConfigProvider>
      <div className={style.sidebar__profileArea}>
        {/* Opens upward inside the sidebar, so it can't run off the screen edge. */}
        {isProfileOpen && (
          <div className={style.sidebar__actions}>
            <Link
              to="/profile"
              className={style.sidebar__actions__item}
              title="Uredi profil"
            >
              <img src="/assets/icons/edit-icon.svg" alt="" />
              {!collapsed && <span>Uredi profil</span>}
            </Link>
            <button
              type="button"
              className={style.sidebar__actions__item}
              onClick={() => logout()}
              title="Odjavi se"
            >
              <img src="/assets/icons/logout-icon.svg" alt="" />
              {!collapsed && <span>Odjavi se</span>}
            </button>
          </div>
        )}
        <button
          type="button"
          className={style.sidebar__profile}
          onClick={() => setIsProfileOpen((open) => !open)}
          aria-expanded={isProfileOpen}
        >
          <span
            className={headerStyle.header__imageContainer}
            style={{ padding: user?.profileImage ? '0' : '8px' }}
          >
            <img
              src={user?.profileImage || '/assets/icons/user.svg'}
              alt="user_icon"
            />
          </span>
          {!collapsed && (
            <span className={style.sidebar__profile__name}>
              {user?.firstName} {user?.lastName}
            </span>
          )}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
